// Escape Bees — a "tap the arrows away to free the trapped bees" puzzle.
//
// The board is a grid packed with long, twisty arrow "snakes" (and a few bees),
// each facing one of four directions. Tap an arrow and it *slithers out head-
// first*, the tail following its own path — but only if the cells directly ahead
// of its head are clear all the way to the edge. Clear the arrows blocking a bee,
// then tap the bee to send it home. Free every bee to finish the level.
//
// Because an arrow leaves head-first, only the cells in front of its HEAD need to
// be clear (the body just follows its own trail). Levels are built by *reverse
// construction*: every arrow is placed with a clear head-lane to the edge, so
// removing arrows in reverse placement order always solves the level — and the
// highest-index remaining arrow is always removable, so you can never get stuck.

export const DIRS = { up: [-1, 0], down: [1, 0], left: [0, -1], right: [0, 1] };
const DIR_KEYS = ["up", "down", "left", "right"];
const cellKey = (r, c) => r + "," + c;

// mulberry32 — small seeded PRNG so a level always regenerates identically.
function mulberry32(a) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function levelParams(level) {
  const arrows = 40 + (level - 1) * 4; // start at 40, grow each level
  const bees = Math.min(14, 1 + Math.floor((level - 1) / 2)); // 1,1,2,2,3…
  const maxLen = Math.min(16, 6 + level); // arrows get longer/twistier with level
  const size = Math.min(22, 16 + Math.floor(level / 2)); // grid grows to stay packed
  return { W: size, H: size, arrows, bees, maxLen };
}

export function generateLevel(level) {
  const P = levelParams(level);
  const { W, H } = P;
  const rng = mulberry32((Math.imul(level, 1000003) ^ 0x9e3779b9) >>> 0);
  const occ = new Map(); // "r,c" -> piece id
  const pieces = [];
  let idc = 1;
  const inB = (r, c) => r >= 0 && r < H && c >= 0 && c < W;

  const commit = (cells, dir, bee) => {
    const id = idc++;
    for (const [r, c] of cells) occ.set(cellKey(r, c), id);
    pieces.push({ id, cells, dir, bee });
    return id;
  };

  // Forward lane from a head cell in `dir` to the edge. Returns the lane cells,
  // or null if any is already occupied.
  const laneFrom = (hr, hc, dir) => {
    const [dr, dc] = DIRS[dir];
    const lane = [];
    let r = hr + dr;
    let c = hc + dc;
    while (inB(r, c)) {
      if (occ.has(cellKey(r, c))) return null;
      lane.push(cellKey(r, c));
      r += dr;
      c += dc;
    }
    return lane;
  };

  const shuffle = (arr) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  // Place one snake: pick a head + facing whose lane to the edge is clear, then
  // grow the body backward with random turns, keeping out of the head's lane.
  const placeSnake = (targetLen, bee) => {
    for (let a = 0; a < 90; a++) {
      const dir = DIR_KEYS[Math.floor(rng() * 4)];
      const [dr, dc] = DIRS[dir];
      const hr = Math.floor(rng() * H);
      const hc = Math.floor(rng() * W);
      if (occ.has(cellKey(hr, hc))) continue;
      const lane = laneFrom(hr, hc, dir);
      if (lane === null) continue;
      if (targetLen <= 1) return commit([[hr, hc]], dir, bee) && 1;
      const forb = new Set(lane);
      const nr = hr - dr;
      const nc = hc - dc; // neck sits behind the head so the head tangent == dir
      if (!inB(nr, nc) || occ.has(cellKey(nr, nc)) || forb.has(cellKey(nr, nc))) continue;
      const body = [[hr, hc], [nr, nc]];
      const used = new Set([cellKey(hr, hc), cellKey(nr, nc)]);
      let cur = [nr, nc];
      while (body.length < targetLen) {
        let moved = false;
        for (const dk of shuffle(DIR_KEYS.slice())) {
          const [er, ec] = DIRS[dk];
          const x = cur[0] + er;
          const y = cur[1] + ec;
          const k = cellKey(x, y);
          if (!inB(x, y) || occ.has(k) || used.has(k) || forb.has(k)) continue;
          body.push([x, y]);
          used.add(k);
          cur = [x, y];
          moved = true;
          break;
        }
        if (!moved) break;
      }
      commit(body.slice().reverse(), dir, bee); // store head-last
      return body.length;
    }
    return 0;
  };

  // 1. Bees first (single cells) — deep in the maze, removed last in a solution.
  let beesPlaced = 0;
  for (let i = 0; i < P.bees; i++) if (placeSnake(1, true) > 0) beesPlaced++;

  // 2. Fill with arrow snakes until the target count.
  let arrows = 0;
  let misses = 0;
  while (arrows < P.arrows && misses < 4000) {
    const targetLen = 2 + Math.floor(rng() * (P.maxLen - 1)); // 2..maxLen
    if (placeSnake(targetLen, false) > 0) {
      arrows++;
      misses = 0;
    } else misses++;
  }

  // 3. Trap any bee that can still walk straight out: drop a 1-cell arrow into
  //    the first open cell of its escape lane (with its own lane clear).
  for (const bee of pieces.filter((p) => p.bee)) {
    if (!pieceCanExit(bee, occ, W, H)) continue;
    const [dr, dc] = DIRS[bee.dir];
    let r = bee.cells[0][0] + dr;
    let c = bee.cells[0][1] + dc;
    while (inB(r, c)) {
      if (!occ.has(cellKey(r, c))) {
        for (const dk of DIR_KEYS) {
          if (laneFrom(r, c, dk) !== null) {
            commit([[r, c]], dk, false);
            break;
          }
        }
        break;
      }
      r += dr;
      c += dc;
    }
  }

  return { W, H, pieces, beesTotal: beesPlaced, arrows };
}

export function occupancy(pieces) {
  const m = new Map();
  for (const p of pieces) for (const [r, c] of p.cells) m.set(cellKey(r, c), p.id);
  return m;
}

// An arrow can slither out iff every cell directly ahead of its HEAD, to the
// edge, is empty (the body follows its own trail, so only the head lane matters).
export function pieceCanExit(piece, occ, W, H) {
  const [dr, dc] = DIRS[piece.dir];
  const head = piece.cells[piece.cells.length - 1];
  let r = head[0] + dr;
  let c = head[1] + dc;
  while (r >= 0 && r < H && c >= 0 && c < W) {
    if (occ.get(cellKey(r, c)) != null) return false;
    r += dr;
    c += dc;
  }
  return true;
}

// Pieces are stored head-last, so the head cell is simply the final cell.
export function headCell(piece) {
  return piece.cells[piece.cells.length - 1];
}

export function newGame(level, beesHome = 0, best = 1) {
  const lvl = generateLevel(level);
  return {
    level,
    W: lvl.W,
    H: lvl.H,
    pieces: lvl.pieces,
    beesTotal: lvl.beesTotal,
    beesThisLevel: 0,
    beesHome, // lifetime
    best: Math.max(best, level),
  };
}

// Tap a piece. If it can slither out, remove it (banking a bee if it was one)
// and report whether that freed the last bee.
export function tap(state, id) {
  const piece = state.pieces.find((p) => p.id === id);
  if (!piece) return { ok: false };
  const occ = occupancy(state.pieces);
  if (!pieceCanExit(piece, occ, state.W, state.H)) {
    return { ok: false, blocked: true, note: piece.bee ? "This bee is still fenced in — clear its path." : "Blocked — an arrow is in its way." };
  }
  const pieces = state.pieces.filter((p) => p.id !== id);
  const st = { ...state, pieces };
  if (piece.bee) {
    st.beesThisLevel = (state.beesThisLevel || 0) + 1;
    st.beesHome = (state.beesHome || 0) + 1;
  }
  const won = !pieces.some((p) => p.bee);
  return { ok: true, state: st, piece, bee: piece.bee, won };
}

export function nextLevel(state) {
  return newGame(state.level + 1, state.beesHome, Math.max(state.best, state.level + 1));
}

export function restart(state) {
  return newGame(state.level, state.beesHome, state.best);
}

// Clamp/validate a saved payload before trusting it.
export function sanitizeProgress(p) {
  return {
    level: Math.max(1, Math.min(100000, (p && p.level) | 0 || 1)),
    beesHome: Math.max(0, Math.min(1e9, (p && p.beesHome) | 0)),
    best: Math.max(1, Math.min(100000, (p && p.best) | 0 || 1)),
  };
}
